import { supabase } from "@/utils/supabase/client";

/**
 * Uploads an image to Supabase storage for a space or widget
 * @param {File} file - The image file to upload
 * @param {string} spaceId - The ID of the space
 * @param {string} [widgetId] - Optional widget ID if uploading for a specific widget
 * @param {string} [folder] - Optional folder name for organizing uploads (e.g. "backgrounds")
 * @returns {Promise<string>} The public URL of the uploaded image
 */
export async function uploadSpaceImage(file, spaceId, widgetId) {
  try {
    // Validate file object and name property
    if (!file) {
      throw new Error("No file provided");
    }

    if (!file.name) {
      throw new Error("File object missing name property");
    }

    const fileExt = file.name.split(".").pop();
    const fileName = `${Date.now()}.${fileExt}`;

    // Log initial parameters
    console.log("Upload attempt:", {
      fileType: file.type,
      fileSize: file.size,
      spaceId,
      widgetId,
    });

    // Get the storage path using appropriate RPC function
    const { data: storagePath, error: pathError } = await supabase.rpc(
      widgetId ? "get_widget_storage_path" : "get_space_storage_path",
      widgetId
        ? { p_space_id: spaceId, p_widget_id: widgetId }
        : { p_space_id: spaceId }
    );

    if (pathError) {
      console.error("Storage path error details:", {
        message: pathError.message,
        code: pathError.code,
        details: pathError.details,
        hint: pathError.hint,
        functionUsed: widgetId
          ? "get_widget_storage_path"
          : "get_space_storage_path",
      });
      throw new Error(`Failed to get storage path: ${pathError.message}`);
    }

    if (!storagePath) {
      throw new Error("Storage path is empty or undefined");
    }

    console.log("Storage path received:", storagePath);

    // Upload the file
    const filePath = `${storagePath}/${fileName}`;
    const { error: uploadError } = await supabase.storage
      .from("spaces")
      .upload(filePath, file, {
        cacheControl: "3600",
        upsert: false,
        contentType: file.type,
      });

    if (uploadError) {
      console.error("Upload error details:", {
        message: uploadError.message,
        statusCode: uploadError?.statusCode,
        path: filePath,
      });
      throw uploadError;
    }

    // Get the public URL
    const {
      data: { publicUrl },
    } = supabase.storage.from("spaces").getPublicUrl(filePath);

    return publicUrl;
  } catch (error) {
    console.error("Upload process error:", {
      message: error.message,
      type: error.name,
      cause: error.cause,
    });
    throw error;
  }
}

/**
 * Uploads media (image, video, or document) to Supabase storage for a space or widget
 * Different file types have different size limits:
 * - Videos: 50MB limit
 * - Documents: 10MB limit
 * - Images: 10MB limit
 * @param {File} file - The file to upload (image, video, or document)
 * @param {string} spaceId - The ID of the space
 * @param {string} [widgetId] - Optional widget ID if uploading for a specific widget
 * @param {string} [folder] - Optional folder name for organizing uploads
 * @returns {Promise<string>} The public URL of the uploaded media
 */
export async function uploadSpaceMedia(file, spaceId, widgetId) {
  try {
    // Validate file object and name property
    if (!file) {
      throw new Error("No file provided");
    }

    if (!file.name) {
      throw new Error("File object missing name property");
    }

    const fileExt = file.name.split(".").pop().toLowerCase();
    const fileName = `${Date.now()}.${fileExt}`;

    // Determine file type category
    const isVideo = file.type.startsWith("video/");
    const isDocument =
      file.type === "application/pdf" ||
      file.type ===
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document" || // docx
      file.type ===
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" || // xlsx
      file.type ===
        "application/vnd.openxmlformats-officedocument.presentationml.presentation" || // pptx
      file.type === "text/plain" ||
      file.type === "text/csv" ||
      fileExt === "pdf" ||
      fileExt === "docx" ||
      fileExt === "xlsx" ||
      fileExt === "pptx" ||
      fileExt === "txt" ||
      fileExt === "csv";

    // Check file size limits
    // For videos: 50MB limit, For documents/images: 10MB limit
    const maxSizeInBytes = isVideo ? 50 * 1024 * 1024 : 10 * 1024 * 1024;

    if (file.size > maxSizeInBytes) {
      throw new Error(
        `File size exceeds the maximum allowed size (${
          isVideo ? "50MB" : "10MB"
        })`
      );
    }

    // Get the storage path using appropriate RPC function
    const { data: storagePath, error: pathError } = await supabase.rpc(
      widgetId ? "get_widget_storage_path" : "get_space_storage_path",
      widgetId
        ? { p_space_id: spaceId, p_widget_id: widgetId }
        : { p_space_id: spaceId }
    );

    if (pathError) {
      console.error("Storage path error details:", {
        message: pathError.message,
        code: pathError.code,
        details: pathError.details,
        hint: pathError.hint,
        functionUsed: widgetId
          ? "get_widget_storage_path"
          : "get_space_storage_path",
      });
      throw new Error(`Failed to get storage path: ${pathError.message}`);
    }

    if (!storagePath) {
      throw new Error("Storage path is empty or undefined");
    }

    // Upload the file
    const filePath = `${storagePath}/${fileName}`;
    const { error: uploadError } = await supabase.storage
      .from("spaces")
      .upload(filePath, file, {
        cacheControl: "3600",
        upsert: false,
        contentType: file.type,
      });

    if (uploadError) {
      console.error("Upload error details:", {
        message: uploadError.message,
        statusCode: uploadError?.statusCode,
        path: filePath,
      });
      throw uploadError;
    }

    // Get the public URL
    const {
      data: { publicUrl },
    } = supabase.storage.from("spaces").getPublicUrl(filePath);

    return publicUrl;
  } catch (error) {
    console.error("Upload process error:", {
      message: error.message,
      type: error.name,
      cause: error.cause,
    });
    throw error;
  }
}

/**
 * Lists images in the default backgrounds folder with pagination
 * @param {number} page - The page number (1-based)
 * @param {number} perPage - Number of items per page
 * @returns {Promise<{images: Array<{name: string, publicUrl: string}>, total: number}>} Paginated array of image objects with total count
 */
export async function listDefaultBackgrounds(page = 1, perPage = 6) {
  try {
    const offset = (page - 1) * perPage;

    // First get total count of all files
    const { data: allFiles, error: countError } = await supabase.storage
      .from("qensa")
      .list("default_backgrounds/images", {
        sortBy: { column: "name", order: "asc" },
      });

    if (countError) {
      console.error("Error getting total count:", countError);
      throw countError;
    }

    const totalCount = allFiles.length;

    // Then get paginated files
    const { data: files, error } = await supabase.storage
      .from("qensa")
      .list("default_backgrounds/images", {
        limit: perPage,
        offset: offset,
        sortBy: { column: "name", order: "asc" },
      });

    if (error) {
      console.error("Error listing default backgrounds:", error);
      throw error;
    }

    // Get public URLs for files
    const images = files.map((file) => {
      const {
        data: { publicUrl },
      } = supabase.storage
        .from("qensa")
        .getPublicUrl(`default_backgrounds/images/${file.name}`);

      return {
        name: file.name,
        publicUrl,
      };
    });

    return {
      images,
      total: totalCount,
    };
  } catch (error) {
    console.error("Error getting default backgrounds:", error);
    throw error;
  }
}

/**
 * Pre-uploads media files during onboarding before space creation
 * Uses the generated UUID as the future space ID
 * @param {Array<{file: File, preview: string, type: string}>} mediaItems - Array of media items to upload
 * @param {Function} onProgress - Progress callback function(current, total)
 * @returns {Promise<{urls: string[], spaceId: string}>} Object containing uploaded URLs and the space ID (UUID)
 */
export async function preUploadOnboardingMedia(mediaItems, onProgress) {
  try {
    // Generate a UUID that will be used as the space ID
    const spaceId = crypto.randomUUID();
    console.log("Generated space ID for media upload:", spaceId);

    const uploadedUrls = [];

    // Upload each media item
    for (let i = 0; i < mediaItems.length; i++) {
      const item = mediaItems[i];

      // Call progress callback
      if (onProgress) {
        onProgress(i, mediaItems.length);
      }

      try {
        // Extract file from the media item
        const file = item.file;
        if (!file) {
          console.warn("Media item missing file object, skipping:", item);
          continue;
        }

        const fileExt = file.name.split(".").pop().toLowerCase();
        const fileName = `${Date.now()}-${i}.${fileExt}`;

        // Upload to the correct path format expected by RLS policies
        // Using 'space' subfolder as per the get_space_storage_path function
        const filePath = `spaces/${spaceId}/space/${fileName}`;

        console.log(
          `Uploading file ${i + 1}/${mediaItems.length} to:`,
          filePath
        );

        const { error: uploadError } = await supabase.storage
          .from("spaces")
          .upload(filePath, file, {
            cacheControl: "3600",
            upsert: false,
            contentType: file.type,
          });

        if (uploadError) {
          console.error(`Upload error for file ${i}:`, uploadError);
          throw uploadError;
        }

        // Get the public URL
        const {
          data: { publicUrl },
        } = supabase.storage.from("spaces").getPublicUrl(filePath);

        uploadedUrls.push(publicUrl);
        console.log(`Successfully uploaded file ${i + 1}:`, publicUrl);
      } catch (error) {
        console.error(`Error uploading media item ${i}:`, error);
        throw new Error(
          `Failed to upload media item ${i + 1}: ${error.message}`
        );
      }
    }

    // Call final progress callback
    if (onProgress) {
      onProgress(mediaItems.length, mediaItems.length);
    }

    return {
      urls: uploadedUrls,
      spaceId: spaceId,
    };
  } catch (error) {
    console.error("Error in preUploadOnboardingMedia:", error);
    throw error;
  }
}

/**
 * Lists all media files for a specific space from Supabase storage
 * Discovers all widget directories (including deleted widgets) and checks for files
 * @param {string} spaceId - The ID of the space
 * @param {Array} widgets - Optional array of current widgets for name mapping
 * @returns {Promise<Array>} Array of file objects with metadata
 */
export async function listSpaceMediaFiles(spaceId, widgets = null) {
  try {
    console.log(`🔍 Starting media file discovery for space: ${spaceId}`);

    // Create a map of widget IDs to widget info for name lookup
    const widgetMap = {};
    if (widgets && Array.isArray(widgets)) {
      widgets.forEach((widget) => {
        widgetMap[widget.id] = {
          name: widget.widget_type?.name || "Widget",
          type: widget.widget_type?.type,
          exists: true,
        };
      });
      console.log(
        `📋 Created widget map for ${widgets.length} current widgets:`,
        Object.keys(widgetMap)
      );
    }

    // Start with space-level files
    console.log(`📁 Checking space-level files at: spaces/${spaceId}/space`);
    const { data: spaceFiles, error: spaceError } = await supabase.storage
      .from("spaces")
      .list(`spaces/${spaceId}/space`, {
        sortBy: { column: "created_at", order: "desc" },
      });

    if (spaceError) {
      console.error("❌ Error listing space files:", spaceError);
    }

    const allFiles = [];

    // Process space-level files
    if (spaceFiles) {
      console.log(`📄 Found ${spaceFiles.length} items in space directory`);
      spaceFiles.forEach((file) => {
        if (file.name && file.name.includes(".")) {
          console.log(`✅ Processing space file: ${file.name}`);
          const filePath = `spaces/${spaceId}/space/${file.name}`;
          const {
            data: { publicUrl },
          } = supabase.storage.from("spaces").getPublicUrl(filePath);

          allFiles.push({
            name: file.name,
            path: filePath,
            publicUrl,
            size: file.metadata?.size || 0,
            mimetype: file.metadata?.mimetype || getFileType(file.name),
            lastModified:
              file.metadata?.lastModified || file.updated_at || file.created_at,
            uploadDate: file.created_at,
            location: "space",
          });
        } else {
          console.log(`⏭️ Skipping non-file item in space: ${file.name}`);
        }
      });
    } else {
      console.log(`📭 No space files found`);
    }

    // Always discover ALL widget directories (including from deleted widgets)
    console.log(
      `🗂️ Discovering all widget directories at: spaces/${spaceId}/widgets`
    );
    const { data: widgetDirs, error: widgetDirsError } = await supabase.storage
      .from("spaces")
      .list(`spaces/${spaceId}/widgets`, {
        sortBy: { column: "created_at", order: "desc" },
      });

    if (widgetDirsError) {
      console.error("❌ Error listing widget directories:", widgetDirsError);
    } else if (widgetDirs) {
      // Debug: log all items to see their structure
      console.log("🔍 Raw widget directory items:", widgetDirs);

      // Get all subdirectories (widget IDs) - these are directories, not files
      // Filter for directories by checking if they don't have a file extension
      const discoveredWidgetDirs = widgetDirs.filter(
        (item) => item.name && !item.name.includes(".")
      );

      console.log(
        `🔍 Discovered ${discoveredWidgetDirs.length} widget directories:`,
        discoveredWidgetDirs.map((dir) => dir.name)
      );

      for (const widgetDir of discoveredWidgetDirs) {
        const widgetId = widgetDir.name;
        const widgetInfo = widgetMap[widgetId];
        const isCurrentWidget = !!widgetInfo;

        console.log(`\n📁 Processing widget directory: ${widgetId}`);
        console.log(
          `   Current widget: ${isCurrentWidget ? "YES" : "NO (deleted/orphaned)"}`
        );
        if (widgetInfo) {
          console.log(`   Widget name: ${widgetInfo.name}`);
          console.log(`   Widget type: ${widgetInfo.type}`);
        }

        try {
          const { data: files, error } = await supabase.storage
            .from("spaces")
            .list(`spaces/${spaceId}/widgets/${widgetId}`, {
              sortBy: { column: "created_at", order: "desc" },
            });

          if (!error && files) {
            console.log(
              `   📄 Found ${files.length} items in widget directory`
            );

            files.forEach((file) => {
              if (file.name && file.name.includes(".")) {
                console.log(`   ✅ Processing widget file: ${file.name}`);
                const filePath = `spaces/${spaceId}/widgets/${widgetId}/${file.name}`;
                const {
                  data: { publicUrl },
                } = supabase.storage.from("spaces").getPublicUrl(filePath);

                allFiles.push({
                  name: file.name,
                  path: filePath,
                  publicUrl,
                  size: file.metadata?.size || 0,
                  mimetype: file.metadata?.mimetype || getFileType(file.name),
                  lastModified:
                    file.metadata?.lastModified ||
                    file.updated_at ||
                    file.created_at,
                  uploadDate: file.created_at,
                  location: "widget",
                  widgetId: widgetId,
                  widgetName: widgetInfo?.name || "Deleted Widget",
                  widgetType: widgetInfo?.type || "unknown",
                  isOrphaned: !isCurrentWidget, // Flag for deleted widgets
                });
              } else {
                console.log(
                  `   ⏭️ Skipping non-file item in widget: ${file.name}`
                );
              }
            });
          } else if (error && !error.message?.includes("not found")) {
            console.warn(
              `   ⚠️ Error listing files for widget ${widgetId}:`,
              error
            );
          } else {
            console.log(
              `   📭 No files found in widget directory (or directory not found)`
            );
          }
        } catch (err) {
          console.warn(
            `   ❌ Failed to process widget directory ${widgetId}:`,
            err
          );
        }
      }
    } else {
      console.log(`📭 No widget directories found`);
    }

    // Sort by upload date (newest first)
    allFiles.sort((a, b) => new Date(b.uploadDate) - new Date(a.uploadDate));

    console.log(`\n📊 MEDIA DISCOVERY SUMMARY:`);
    console.log(`   Total files found: ${allFiles.length}`);
    console.log(
      `   Space files: ${allFiles.filter((f) => f.location === "space").length}`
    );
    console.log(
      `   Widget files: ${allFiles.filter((f) => f.location === "widget").length}`
    );
    console.log(
      `   Files from current widgets: ${allFiles.filter((f) => f.location === "widget" && !f.isOrphaned).length}`
    );
    console.log(
      `   Files from deleted widgets: ${allFiles.filter((f) => f.location === "widget" && f.isOrphaned).length}`
    );

    return allFiles;
  } catch (error) {
    console.error("Error listing space media files:", error);
    throw error;
  }
}

/**
 * Helper function to determine file type from extension
 * @param {string} filename - The filename
 * @returns {string} The MIME type
 */
function getFileType(filename) {
  const ext = filename.split(".").pop()?.toLowerCase();
  const mimeTypes = {
    jpg: "image/jpeg",
    jpeg: "image/jpeg",
    png: "image/png",
    gif: "image/gif",
    webp: "image/webp",
    mp4: "video/mp4",
    webm: "video/webm",
    ogg: "video/ogg",
    pdf: "application/pdf",
    docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
    txt: "text/plain",
    csv: "text/csv",
  };
  return mimeTypes[ext] || "application/octet-stream";
}

/**
 * Checks which media files are currently being used by the space or its widgets
 * @param {Array} mediaFiles - Array of media file objects
 * @param {Object} spaceData - The space data object with widgets and background
 * @returns {Object} Object mapping file URLs to usage information
 */
export function checkMediaUsage(mediaFiles, spaceData) {
  const usageMap = {};

  // Initialize all files as unused
  mediaFiles.forEach((file) => {
    usageMap[file.publicUrl] = {
      isUsed: false,
      usedBy: [],
    };
  });

  // Check space background
  const spaceBackground = spaceData?.space_data?.theme?.background;
  if (spaceBackground?.media?.url) {
    if (usageMap[spaceBackground.media.url]) {
      usageMap[spaceBackground.media.url].isUsed = true;
      usageMap[spaceBackground.media.url].usedBy.push({
        type: "background",
        name: "Space Background",
      });
    }
  }

  // Check widgets
  const widgets = spaceData?.widgets || [];
  widgets.forEach((widget) => {
    const config = widget.config || {};

    // Check single media widgets
    if (config.media?.url && usageMap[config.media.url]) {
      usageMap[config.media.url].isUsed = true;
      usageMap[config.media.url].usedBy.push({
        type: "widget",
        name: widget.widget_type?.name || "Media Widget",
        widgetId: widget.id,
      });
    }

    // Check image galleries
    if (config.images && Array.isArray(config.images)) {
      config.images.forEach((image) => {
        const imageUrl = typeof image === "string" ? image : image?.url;
        if (imageUrl && usageMap[imageUrl]) {
          usageMap[imageUrl].isUsed = true;
          usageMap[imageUrl].usedBy.push({
            type: "widget",
            name: widget.widget_type?.name || "Image Gallery",
            widgetId: widget.id,
          });
        }
      });
    }

    // Check file widgets
    if (config.file?.url && usageMap[config.file.url]) {
      usageMap[config.file.url].isUsed = true;
      usageMap[config.file.url].usedBy.push({
        type: "widget",
        name: widget.widget_type?.name || "File Widget",
        widgetId: widget.id,
      });
    }

    // Check bio widget images
    if (config.image?.url && usageMap[config.image.url]) {
      usageMap[config.image.url].isUsed = true;
      usageMap[config.image.url].usedBy.push({
        type: "widget",
        name: widget.widget_type?.name || "Bio Widget",
        widgetId: widget.id,
      });
    }

    // Handle legacy image field
    if (
      config.image &&
      typeof config.image === "string" &&
      usageMap[config.image]
    ) {
      usageMap[config.image].isUsed = true;
      usageMap[config.image].usedBy.push({
        type: "widget",
        name: widget.widget_type?.name || "Widget",
        widgetId: widget.id,
      });
    }
  });

  return usageMap;
}
